"use client";

import type { KundaliResponse } from "@/lib/api/kundali";
import { RASHI_NAMES, PLANET_MAP } from "@/lib/planet-display";
import { SacredCard } from "@/components/ui/SacredPrimitives";

/**
 * CareerSnapshot — the FREE 10th-house (karma bhav) + career-lord teaser.
 *
 * Rules 24/29 (engine computes, LLM frames, never a literal): EVERY astrological
 * value rendered here is read straight from the engine's `KundaliResponse`
 * (house.sign, house.lord, planet.house, planet.is_retrograde/exalted/debilitated)
 * — this component never computes a date/sign/score and never lets an LLM emit one.
 * RASHI_NAMES / PLANET_MAP are display-name lookups keyed on engine ids only.
 *
 * NET-ZERO teaser (rule 46/depth-preview): career has no free tool today, so this
 * thin snapshot (the 10th house + its lord placement + a templated factual read)
 * is intentionally net-new. The DEPTH stays paid — D10/Dasamsa, dasha timing,
 * career yogas, and upays are described-only in the DepthPaywall below, never shown.
 *
 * Presentational only — no offer/impression/payment logic lives here.
 */

const TENTH_HOUSE = 10;

type Lang = "en" | "hi";
type LordPlanet = KundaliResponse["planets"][number];

function rashiLabel(signId: string | undefined, language: Lang): string | null {
  if (!signId) return null;
  const entry = RASHI_NAMES[signId.toLowerCase()];
  if (!entry) return null;
  return language === "hi" ? entry.hi : entry.en;
}

function planetLabel(planetId: string | undefined, language: Lang): string | null {
  if (!planetId) return null;
  const entry = PLANET_MAP[planetId.toLowerCase()];
  if (!entry) return null;
  // Prefer the Vedic graha name (Surya/Shani) which both languages use in copy
  // (rule 21); fall back to the English display name.
  return language === "hi" ? entry.name_hi || entry.name_en : entry.name_en || entry.name_hi;
}

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

interface CareerSnapshotModel {
  tenthSign: string;
  lordName: string | null;
  lordHouse: number | null;
  lordPlanet: LordPlanet | undefined;
  planetsInTenth: string[];
}

/** Pure read of the engine result into the snapshot fields (no interpretation). */
function buildModel(result: KundaliResponse, language: Lang): CareerSnapshotModel | null {
  const tenth = result.houses.find((h) => h.house_number === TENTH_HOUSE);
  const tenthSign = rashiLabel(tenth?.sign, language);
  if (!tenth || !tenthSign) return null; // birth time unknown → no house framework; degrade honestly
  const lordId = tenth.lord;
  const lordPlanet = lordId
    ? result.planets.find((p) => p.planet_id.toLowerCase() === lordId.toLowerCase())
    : undefined;
  return {
    tenthSign,
    lordName: planetLabel(lordId, language),
    lordHouse: lordPlanet?.house ?? null,
    lordPlanet,
    planetsInTenth: (tenth.planets ?? [])
      .map((p) => planetLabel(p, language))
      .filter((p): p is string => Boolean(p)),
  };
}

export default function CareerSnapshot({
  result,
  language,
}: {
  result: KundaliResponse;
  language: Lang;
}) {
  const model = buildModel(result, language);
  if (!model) return null;

  return (
    <SacredCard className="mx-auto mb-4 max-w-md p-5 text-left sm:p-6">
      <p
        className="mb-3 text-[11px] font-bold uppercase tracking-wider"
        style={{ color: "var(--intent-brand-text)", letterSpacing: "0.12em" }}
      >
        {language === "hi" ? "करियर झलक" : "Career snapshot"}
      </p>
      <h3
        className="mb-3 text-[17px] font-bold sm:text-[19px]"
        style={{ color: "var(--ish-dark)", fontFamily: "PT Serif, Georgia, serif" }}
      >
        {language === "hi"
          ? "आपका दशम भाव (कर्म भाव) — फ्री झलक"
          : "Your 10th house (karma bhav) — free snapshot"}
      </h3>
      <SnapshotRows model={model} language={language} />
      <p className="mt-3 text-caption leading-relaxed" style={{ color: "var(--ish-text-faint)" }}>
        {oneLineRead(model, language)}
      </p>
    </SacredCard>
  );
}

function SnapshotRows({ model, language }: { model: CareerSnapshotModel; language: Lang }) {
  const { tenthSign, lordName, lordHouse, planetsInTenth } = model;
  return (
    <ul className="space-y-2 text-body-small" style={{ color: "var(--ish-text-muted)" }}>
      <SnapshotRow
        label={language === "hi" ? "दशम भाव राशि" : "10th house sign"}
        value={tenthSign}
      />
      {lordName ? (
        <SnapshotRow
          label={language === "hi" ? "कर्म स्वामी (10th lord)" : "Career lord (10th lord)"}
          value={lordValue(lordName, lordHouse, language)}
        />
      ) : null}
      {planetsInTenth.length > 0 ? (
        <SnapshotRow
          label={language === "hi" ? "दशम भाव में ग्रह" : "Planets in the 10th"}
          value={planetsInTenth.join(", ")}
        />
      ) : null}
    </ul>
  );
}

function lordValue(lordName: string, lordHouse: number | null, language: Lang): string {
  if (!lordHouse) return lordName;
  return language === "hi"
    ? `${lordName} — ${lordHouse}वें भाव में`
    : `${lordName} — placed in the ${ordinal(lordHouse)} house`;
}

function SnapshotRow({ label, value }: { label: string; value: string }) {
  return (
    <li className="flex items-baseline justify-between gap-3">
      <span style={{ color: "var(--ish-text-faint)" }}>{label}</span>
      <span className="text-right font-semibold" style={{ color: "var(--ish-dark)" }}>
        {value}
      </span>
    </li>
  );
}

// The engine's own dignity flags for the career lord, surfaced verbatim — never
// an astrology judgment this component invents (rules 24/29).
function dignityNote(lordPlanet: LordPlanet | undefined, language: Lang): string {
  if (!lordPlanet) return "";
  const labels: string[] = [];
  if (lordPlanet.is_exalted) labels.push(language === "hi" ? "उच्च का" : "exalted");
  if (lordPlanet.is_debilitated) labels.push(language === "hi" ? "नीच का" : "debilitated");
  if (lordPlanet.is_retrograde) labels.push(language === "hi" ? "वक्री" : "retrograde");
  return labels.length ? ` (${labels.join(", ")})` : "";
}

/**
 * A factual one-line read assembled ENTIRELY from engine fields — never an
 * astrology judgment this component invents (rules 22/24/29). It names the
 * 10th-house sign + the career lord's house placement and surfaces the engine's
 * own dignity flags, then points to the paid depth for the actual interpretation.
 */
function oneLineRead(model: CareerSnapshotModel, language: Lang): string {
  const { tenthSign, lordName, lordHouse, lordPlanet } = model;
  const note = dignityNote(lordPlanet, language);
  const hasLord = Boolean(lordName && lordHouse);
  if (language === "hi") {
    const lordPart = hasLord
      ? `आपका कर्म स्वामी ${lordName}${note} ${lordHouse}वें भाव में बैठा है।`
      : "";
    return `आपका करियर ${tenthSign} दशम भाव से चलता है। ${lordPart} पूरी टाइमिंग, दशा और उपाय आपकी फुल रिपोर्ट में हैं।`;
  }
  const lordPart = hasLord
    ? `Your career lord ${lordName}${note} sits in the ${ordinal(lordHouse as number)} house.`
    : "";
  return `Your career direction runs through the 10th house in ${tenthSign}. ${lordPart} The full timing, dasha windows and upays are in your complete report.`;
}
